import React, { useMemo, useState } from 'react';

import Axios from 'axios';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';

import { useProjectContext, useProjectContextDispatch, reloadProjectAccountingData } from '@contexts/ProjectContext';

import { forceReloadProject } from '@hooks/API/Project/UseProject';

import { Loading } from '@components/Gui/Loading';
import { NwButton } from '@components/Gui/NwButton';
import FamiliesSelector from '@components/Transactions/FamiliesSelector';
import TransactionDetailDrawer from '@components/Transactions/TransactionsDrawer/TransactionDetailDrawer';
import TransactionsStatusLegend from '@components/Transactions/TransactionsStatusLegend';

import ProjectGroupedTransactionsListItem from './ProjectGroupedTransactionsListItem';
import ProjectTransactionsTotalsBand from './ProjectTransactionsTotalsBand';
import { ProjectTransactionListContainer } from './ProjectTransactionsUI';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const BillableTrigger = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 1.5rem 1rem 0 0;
`;

const ProjectGroupedTransactionsList = ({ readOnly, isExpanded, event }) => {
    const { projectId, projectData: project, transactionsGrouped } = useProjectContext();
    const projectDispatch = useProjectContextDispatch();

    const [showTransactionDetail, setShowTransactionDetail] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState();
    const [selectedFamilies, setSelectedFamilies] = useState([]);
    const [settingAsBillable, setSettingAsBillable] = useState(false);

    const transactions = transactionsGrouped;
    const isFetchingTransactions = !transactionsGrouped;
    const isFetchingProject = !project;

    const reloadTransactions = () => {
        if (projectId) {
            reloadProjectAccountingData(projectId, projectDispatch);
        }
    };

    const dateToString = (date) => {
        if (date) {
            return getMoment(date).format('YYYYMMDD');
        } else {
            return '99999999';
        }
    };

    const usedFamilies = useMemo(() => {
        let families = [];
        if (transactions && transactions.length) {
            for (const modeltransaction of transactions) {
                if (modeltransaction.Dates) {
                    for (const date of modeltransaction.Dates) {
                        if (date.Transactions) {
                            for (const transaction of date.Transactions) {
                                if (!families.includes(transaction.Family)) {
                                    families.push(transaction.Family);
                                }
                            }
                        }
                    }
                }
            }
        }
        return families;
    }, [transactions]);

    const sortedDates = useMemo(() => {
        const sortedArray = [];
        if (transactions && transactions.length) {
            for (const modeltransaction of transactions) {
                if (modeltransaction.Dates && modeltransaction.Dates.length) {
                    const sorteddates = modeltransaction.Dates.sort(
                        (a, b) => dateToString(a.Date) - dateToString(b.Date)
                    );
                    sortedArray.push({
                        ...modeltransaction,
                        Dates: sorteddates,
                    });
                } else {
                    sortedArray.push(modeltransaction);
                }
            }
        }
        return sortedArray;
    }, [transactions]);

    const filteredTransactions = useMemo(() => {
        let filteredTransactions = [];
        if (sortedDates && sortedDates.length) {
            if (selectedFamilies.length) {
                for (const modeltransaction of sortedDates) {
                    if (modeltransaction.Dates && modeltransaction.Dates.length) {
                        const filtereddates = modeltransaction.Dates.map((date) => {
                            if (date.Transactions && date.Transactions.length) {
                                const filteredtransactions = date.Transactions.filter((transaction) =>
                                    selectedFamilies.includes(transaction.Family)
                                );
                                return {
                                    ...date,
                                    Transactions: filteredtransactions,
                                };
                            } else {
                                return date;
                            }
                        }).filter((date) => date.Transactions && date.Transactions.length);
                        filteredTransactions.push({
                            ...modeltransaction,
                            Dates: filtereddates,
                        });
                    } else {
                        filteredTransactions.push(modeltransaction);
                    }
                }
                //remove modeltransactions without dates
                filteredTransactions = filteredTransactions.filter(
                    (modeltransaction) => modeltransaction.Dates && modeltransaction.Dates.length
                );
            } else {
                filteredTransactions = sortedDates;
            }
        }
        return filteredTransactions;
    }, [sortedDates, selectedFamilies]);

    const getFilteredTransactionsByEvent = (transactions) => {
        return transactions.filter((transaction) => transaction[event.elType + 'ID'] === event.ID);
    };

    const eventTransactions = useMemo(() => {
        let eventTransactions = [];
        if (sortedDates && sortedDates.length) {
            if (event) {
                for (const modeltransaction of sortedDates) {
                    if (modeltransaction.Dates && modeltransaction.Dates.length) {
                        const filtereddates = modeltransaction.Dates.map((date) => {
                            if (date.Transactions && date.Transactions.length) {
                                const filteredtransactions = getFilteredTransactionsByEvent(date.Transactions);
                                return {
                                    ...date,
                                    Transactions: filteredtransactions,
                                };
                            } else {
                                return date;
                            }
                        }).filter((date) => date.Transactions && date.Transactions.length);
                        eventTransactions.push({
                            ...modeltransaction,
                            Dates: filtereddates,
                        });
                    } else {
                        eventTransactions.push(modeltransaction);
                    }
                }
                //remove modeltransactions without dates
                eventTransactions = eventTransactions.filter(
                    (modeltransaction) => modeltransaction.Dates && modeltransaction.Dates.length
                );
            }
        }
        return eventTransactions;
    }, [sortedDates]);

    const otherTransactions = useMemo(() => {
        let otherTransactions = [];
        if (sortedDates && sortedDates.length) {
            if (event) {
                for (const modeltransaction of sortedDates) {
                    if (modeltransaction.Dates && modeltransaction.Dates.length) {
                        const filtereddates = modeltransaction.Dates.map((date) => {
                            if (date.Transactions && date.Transactions.length) {
                                const filteredtransactions = date.Transactions.filter(
                                    (transaction) => !transaction.EventID || transaction.EventID !== event.ID
                                );
                                return {
                                    ...date,
                                    Transactions: filteredtransactions,
                                };
                            } else {
                                return date;
                            }
                        }).filter((date) => date.Transactions && date.Transactions.length);
                        otherTransactions.push({
                            ...modeltransaction,
                            Dates: filtereddates,
                        });
                    } else {
                        otherTransactions.push(modeltransaction);
                    }
                }
                //remove modeltransactions without dates
                otherTransactions = otherTransactions.filter(
                    (modeltransaction) => modeltransaction.Dates && modeltransaction.Dates.length
                );
            }
        }
        return otherTransactions;
    }, [sortedDates]);

    const handleEditTransaction = (transaction) => {
        setSelectedTransaction(transaction);
        setShowTransactionDetail(true);
    };

    const handleTransactionCreatedOrUpdated = async () => {
        reloadTransactions();
        if (project?.ID) {
            const updatedProject = await forceReloadProject(project.ID);
            projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
        }
    };

    const handleCloseTransactionDetail = () => {
        setSelectedTransaction();
        setShowTransactionDetail(false);
    };

    const handleSelectFamily = (family) => {
        if (selectedFamilies.includes(family)) {
            setSelectedFamilies(selectedFamilies.filter((f) => f !== family));
        } else {
            setSelectedFamilies([...selectedFamilies, family]);
        }
    };

    const setAllTransactionsToBillable = async () => {
        setSettingAsBillable(true);
        //const statusMoreThanBillable = ["Billable", "Locked", "Invoiced"]
        let rowsUpdatedCounter = 0;
        for (const m of filteredTransactions) {
            for (const d of m.Dates) {
                for (const t of d.Transactions) {
                    if (t.ClientStatements && t.ClientStatements.length) {
                        //if (!t.ClientStatements.some(statement => statusMoreThanBillable.includes(statement.Status))) {
                        //consider only confirmed (and not cancelled) transactions
                        if (t.ClientStatements.some((statement) => statement.Status === 'Job')) {
                            if (!t.WorkDateCancelled) {
                                await Axios.put(`/transactions/${t.Family}/setasbillable/${t.ID}`);
                                rowsUpdatedCounter++;
                            }
                        }
                    }
                }
            }
        }
        if (rowsUpdatedCounter > 0) {
            reloadTransactions();
        }
        setSettingAsBillable(false);
    };

    if (isFetchingTransactions || isFetchingProject) {
        return <Loading paddingTop="4rem" />;
    }

    return filteredTransactions && project ? (
        <ProjectTransactionListContainer>
            <ProjectTransactionsTotalsBand transactions={transactions} eventDrawer={event} />
            {!event && (
                <FamiliesSelector
                    families={usedFamilies}
                    selectedFamilies={selectedFamilies}
                    onSelectFamily={handleSelectFamily}
                    onSelectAll={() => setSelectedFamilies([])}
                />
            )}
            {event ? (
                <div className="transactions-list-for-event-drawer">
                    <h1>Event transactions</h1>
                    {eventTransactions.map((model, index) => (
                        <ProjectGroupedTransactionsListItem
                            key={`model-${model.ModelID ? model.ModelID : '0'}`}
                            model={model}
                            project={project}
                            isExpanded={isExpanded}
                            onEditTransaction={handleEditTransaction}
                            readOnly={readOnly}
                            eventDrawer
                            // showTotals
                        />
                    ))}
                    <br />
                    <h1>Other transactions in project</h1>
                    {otherTransactions.map((model, index) => (
                        <ProjectGroupedTransactionsListItem
                            key={`model-${model.ModelID ? model.ModelID : '0'}`}
                            model={model}
                            project={project}
                            isExpanded={isExpanded}
                            onEditTransaction={handleEditTransaction}
                            readOnly={readOnly}
                            eventDrawer
                            // showTotals
                        />
                    ))}
                </div>
            ) : (
                <>
                    <BillableTrigger>
                        <NwButton
                            primary
                            icon={byPrefixAndName.fass['bolt']}
                            loading={settingAsBillable}
                            label="set all confirmed transactions as billable"
                            onClick={setAllTransactionsToBillable}
                        />
                    </BillableTrigger>
                    {settingAsBillable ? (
                        <div className="loading-container">
                            <Loading textBlack text="applying changes..." />
                        </div>
                    ) : (
                        <>
                            {filteredTransactions.map((model, index) => (
                                <ProjectGroupedTransactionsListItem
                                    key={`model-${model.ModelID ? model.ModelID : '0'}`}
                                    model={model}
                                    project={project}
                                    isExpanded={isExpanded}
                                    onEditTransaction={handleEditTransaction}
                                    readOnly={readOnly}
                                    // showTotals
                                />
                            ))}
                        </>
                    )}
                </>
            )}
            <div className="transactions-status-legend-container">
                <TransactionsStatusLegend />
            </div>
            {showTransactionDetail && (
                <TransactionDetailDrawer
                    hideProject
                    transaction={selectedTransaction}
                    projectId={project.ID}
                    // callsheets={project.Callsheets}
                    // clientID={project.CustomerID}
                    onClose={handleCloseTransactionDetail}
                    onUpdateTransaction={handleTransactionCreatedOrUpdated}
                    onTransactionRemoved={async () => {
                        setShowTransactionDetail(false);
                        setSelectedTransaction();

                        if (project?.ID) {
                            // Reload both project data and accounting data together
                            const updatedProject = await forceReloadProject(project.ID);
                            projectDispatch({ type: 'UpdateProjectData', projectData: updatedProject });
                            await reloadProjectAccountingData(project.ID, projectDispatch);
                        }
                    }}
                />
            )}
        </ProjectTransactionListContainer>
    ) : (
        <Loading textBlack />
    );
};

export default ProjectGroupedTransactionsList;
